import {
    getAttendeeTimezone,
    shouldShowDateInAttendeeTimezone,
} from '@eventbrite/event-date-time';
import moment from 'moment-timezone';
import { LocalizedDateTime } from '../../types';

export const getLocalizedDate = (
    date: LocalizedDateTime,
    isOnlineEvent: boolean,
) => {
    const userTimezone = getAttendeeTimezone();

    if (shouldShowDateInAttendeeTimezone({ isOnlineEvent, userTimezone })) {
        return moment.tz(date.utc, userTimezone);
    }

    return moment(date.local);
};

export const getFormattedDate = ({
    date,
    format,
    isOnlineEvent,
    localizedFormat,
    locale,
}: {
    date: LocalizedDateTime;
    isOnlineEvent: boolean;
    format: string;
    locale: string;
    localizedFormat?: string;
}) => {
    const attendeeTimezone = getAttendeeTimezone();
    const formatToApply = shouldShowDateInAttendeeTimezone({
        isOnlineEvent,
        userTimezone: attendeeTimezone,
    })
        ? localizedFormat ?? format
        : format;

    return getLocalizedDate(date, isOnlineEvent)
        .locale(locale)
        .format(formatToApply);
};

export const getFormattedDateCard = ({
    date,
    isOnlineEvent,
    locale,
}: {
    date: LocalizedDateTime;
    isOnlineEvent: boolean;
    locale: string;
}) => {
    const dateToMoment = getLocalizedDate(date, isOnlineEvent).locale(locale);

    return {
        weekDay: dateToMoment.format('ddd'),
        fullWeekDay: dateToMoment.format('dddd'),
        monthAndDay: dateToMoment.format('M/D'),
        time: dateToMoment.format('h:mma'),
    };
};

const TIMEZONE_FORMAT = 'z';
export const getUTCTimezone = ({
    date,
    isOnlineEvent,
    locale,
    isRepeatingChildEventsSliderExperiment,
}: {
    date: LocalizedDateTime;
    isOnlineEvent: boolean;
    locale: string;
    isRepeatingChildEventsSliderExperiment: boolean;
}) => {
    if (!isRepeatingChildEventsSliderExperiment) {
        return null;
    }

    const attendeeTimezone = getAttendeeTimezone();

    const timezone = shouldShowDateInAttendeeTimezone({
        isOnlineEvent,
        userTimezone: attendeeTimezone,
    })
        ? getAttendeeTimezone()
        : date.timezone;

    return moment.tz(date.utc, timezone).locale(locale).format(TIMEZONE_FORMAT);
};

export const isSameDay = ({
    end,
    isOnlineEvent,
    start,
}: {
    end: LocalizedDateTime;
    start: LocalizedDateTime;
    isOnlineEvent: boolean;
}) =>
    getLocalizedDate(start, isOnlineEvent)
        .startOf('day')
        .diff(getLocalizedDate(end, isOnlineEvent).startOf('day'), 'days') ===
    0;
